/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Currencies Service - Currency Management API
 * ============================================================================
 *
 * Service layer for currency reference data API calls.
 * Used for organization billing currency selection.
 *
 * ============================================================================
 */

import { get, post, patch, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';

/**
 * Currency DTO from backend
 */
export interface CurrencyDto {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isActive: boolean;
  exchangeRateToUsd: number;
}

/**
 * Request DTO for creating a new currency
 */
export interface CreateCurrencyRequest {
  code: string;
  name: string;
  symbol: string;
  decimalPlaces?: number;
  exchangeRateToUsd?: number;
}

/**
 * Request DTO for updating exchange rate
 */
export interface UpdateExchangeRateRequest {
  exchangeRateToUsd: number;
}

const BASE_URL = '/currencies';

/**
 * Currencies service
 */
export const currenciesService = {
  /**
   * Get all active currencies
   * Available to all authenticated users
   */
  async getActiveCurrencies(): Promise<CurrencyDto[]> {
    const response = await get<ApiResponse<CurrencyDto[]>>(BASE_URL);
    return response.data;
  },

  /**
   * Get all currencies including inactive (SuperAdmin only)
   */
  async getAllCurrencies(): Promise<CurrencyDto[]> {
    const response = await get<ApiResponse<CurrencyDto[]>>(`${BASE_URL}/all`);
    return response.data;
  },

  /**
   * Get currency by ID
   */
  async getCurrencyById(id: string): Promise<CurrencyDto> {
    const response = await get<ApiResponse<CurrencyDto>>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Get currency by code
   */
  async getCurrencyByCode(code: string): Promise<CurrencyDto> {
    const response = await get<ApiResponse<CurrencyDto>>(`${BASE_URL}/code/${code}`);
    return response.data;
  },

  /**
   * Create a new currency (SuperAdmin only)
   */
  async createCurrency(data: CreateCurrencyRequest): Promise<CurrencyDto> {
    const response = await post<ApiResponse<CurrencyDto>>(BASE_URL, data);
    return response.data;
  },

  /**
   * Toggle currency active status (SuperAdmin only)
   */
  async toggleActive(id: string): Promise<CurrencyDto> {
    const response = await patch<ApiResponse<CurrencyDto>>(`${BASE_URL}/${id}/toggle`);
    return response.data;
  },

  /**
   * Update exchange rate for a currency (SuperAdmin only)
   */
  async updateExchangeRate(id: string, data: UpdateExchangeRateRequest): Promise<CurrencyDto> {
    const response = await patch<ApiResponse<CurrencyDto>>(`${BASE_URL}/${id}/exchange-rate`, data);
    return response.data;
  },

  /**
   * Delete a currency (SuperAdmin only)
   * Will fail if currency is in use by organizations
   */
  async deleteCurrency(id: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/${id}`);
  },
};

export default currenciesService;
